import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import notificationService from '../../services/notificationService';

function safeParseJson(maybeJson) {
  if (!maybeJson) return {};
  if (typeof maybeJson === 'object') return maybeJson;
  if (typeof maybeJson !== 'string') return {};
  try {
    return JSON.parse(maybeJson);
  } catch (_) {
    return {};
  }
}

function getExplicitMetaSummary(metaObj) {
  const s =
    metaObj?.summary ||
    metaObj?.summaryText ||
    metaObj?.summary_text ||
    metaObj?.shortSummary ||
    null;
  if (typeof s === 'string' && s.trim()) return s.trim();
  return '';
}

function getListSummary(metaObj, message) {
  const explicit = getExplicitMetaSummary(metaObj);
  if (explicit) return explicit;

  const msg = typeof message === 'string' ? message : '';
  const cleaned = msg.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.length > 160 ? `${cleaned.slice(0, 157)}...` : cleaned;
}

function getMetaImages(metaObj) {
  return (
    metaObj?.images ||
    metaObj?.imageUrls ||
    metaObj?.image_urls ||
    metaObj?.media?.images ||
    metaObj?.media?.imageUrls ||
    []
  );
}

function getMetaVideoUrl(metaObj) {
  return (
    metaObj?.videoUrl ||
    metaObj?.video_url ||
    metaObj?.video ||
    metaObj?.media?.videoUrl ||
    metaObj?.media?.video_url ||
    metaObj?.media?.video ||
    null
  );
}

const NotificationsPopup = ({
  isOpen,
  unreadCount,
  onUnreadCountChange,
  containerStyle,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 8;

  const [selectedId, setSelectedId] = useState(null);
  const selectedItem = useMemo(() => items.find((n) => n.id === selectedId) || null, [items, selectedId]);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await notificationService.getInbox({ unreadOnly, limit: PAGE_SIZE, page: 1 });
      setItems(data?.notifications || []);
      const fetchedCount = Array.isArray(data?.notifications) ? data.notifications.length : 0;
      const total = typeof data?.total === 'number' ? data.total : fetchedCount;
      setHasMore(fetchedCount < total);
      setPage(1);
      if (typeof data?.unread === 'number') {
        onUnreadCountChange?.(data.unread);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, onUnreadCountChange]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data = await notificationService.getInbox({ unreadOnly, limit: PAGE_SIZE, page: nextPage });
      const nextItems = Array.isArray(data?.notifications) ? data.notifications : [];

      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        for (const n of nextItems) {
          if (!seen.has(n.id)) merged.push(n);
        }
        return merged;
      });

      const total = typeof data?.total === 'number' ? data.total : 0;
      const loadedCountAfterAppend = (items.length + nextItems.length);
      setHasMore(total > 0 ? loadedCountAfterAppend < total : nextItems.length === PAGE_SIZE);
      setPage(nextPage);
      if (typeof data?.unread === 'number') {
        onUnreadCountChange?.(data.unread);
      }
    } catch (_) {
      // Keep list usable even if pagination call fails.
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, items.length, loading, loadingMore, onUnreadCountChange, page, unreadOnly]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    loadInbox();
  }, [isOpen, unreadOnly, loadInbox]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  const markAllRead = useCallback(async () => {
    try {
      const res = await notificationService.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));

      if (typeof res?.unread === 'number') onUnreadCountChange?.(res.unread);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to mark all as read');
    }
  }, [onUnreadCountChange]);

  const onOpenNotification = useCallback(
    async (item) => {
      setSelectedId(item.id);

      // If unread, mark read immediately for a clean UX.
      if (!item.read_at) {
        try {
          const res = await notificationService.markRead([item.id]);
          setItems((prev) =>
            prev.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n))
          );
          if (typeof res?.unread === 'number') onUnreadCountChange?.(res.unread);
        } catch (err) {
          setError(err?.response?.data?.message || 'Failed to mark notification as read');
        }
      }
    },
    [onUnreadCountChange]
  );

  const headerUnread = typeof unreadCount === 'number' ? unreadCount : 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-[12px] shadow-xl overflow-hidden"
      style={{
        fontFamily: 'Raleway, sans-serif',
        width: selectedItem
          ? 'min(560px, calc(100vw - 32px))'
          : (containerStyle?.width ?? 'min(380px, calc(100vw - 32px))'),
        maxWidth: containerStyle?.maxWidth ?? 'calc(100vw - 32px)',
        minWidth: containerStyle?.minWidth ?? 240,
        height: selectedItem
          ? 'min(78vh, 760px)'
          : (containerStyle?.height ?? 520),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[#0f172a] font-semibold">Notifications</div>
            <div className="text-xs text-[#64748b] mt-0.5">
              {headerUnread > 0 ? `${headerUnread} unread` : 'All caught up'}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="text-xs border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 transition-colors text-[#0f172a]"
              onClick={markAllRead}
              disabled={headerUnread <= 0}
              title="Mark all as read"
            >
              Mark Read
            </button>

            <button
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                unreadOnly ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200'
              }`}
              onClick={() => setUnreadOnly((v) => !v)}
              title="Filter unread"
            >
              Unread
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="px-3 py-2 text-xs text-red-600 border-b border-gray-100">{error}</div> : null}
      {loading ? <div className="px-3 py-2 text-xs text-[#475569] border-b border-gray-100">Loading…</div> : null}

      <div className="flex-1 overflow-y-auto">
        {!loading && items.length === 0 ? (
          <div className="px-3 py-3">
            <div className="text-sm text-[#475569]">No notifications yet.</div>
          </div>
        ) : null}

        {!loading && items.length > 0 ? (
          <>
            {selectedItem ? (
              <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button
                    className="text-xs text-[#4B57AA] hover:underline inline-flex items-center gap-1"
                    onClick={() => setSelectedId(null)}
                  >
                    <ChevronLeft size={14} /> Back
                  </button>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <div className="text-[#0f172a] font-semibold">{selectedItem.title}</div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[10px] uppercase text-[#334155]">
                      {selectedItem.severity}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[10px] uppercase text-[#4B57AA]">
                      {selectedItem.type || 'notification'}
                    </span>
                  </div>
                  <div className="text-xs text-[#64748b] mt-1">
                    {selectedItem.sent_at ? new Date(selectedItem.sent_at).toLocaleString() : ''}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-sm text-[#334155] whitespace-pre-wrap leading-6">
                {selectedItem.message}
              </div>

              {(() => {
                const metaObj = safeParseJson(selectedItem?.meta);
                const summary = getExplicitMetaSummary(metaObj);
                const images = Array.isArray(getMetaImages(metaObj)) ? getMetaImages(metaObj) : [];
                const videoUrl = getMetaVideoUrl(metaObj);

                return (
                  <div className="mt-4 space-y-3">
                    {summary ? (
                      <div className="bg-gray-50 border border-gray-100 rounded p-3">
                        <div className="text-xs uppercase tracking-wide text-[#64748b]">Summary</div>
                        <div className="text-sm text-[#334155] whitespace-pre-wrap">{summary}</div>
                      </div>
                    ) : null}

                    {videoUrl ? (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-2">
                          <VideoIcon size={14} /> Video
                        </div>
                        <video controls src={videoUrl} className="mt-2 w-full rounded border border-gray-200" />
                      </div>
                    ) : null}

                    {images.length > 0 ? (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-[#64748b] flex items-center gap-2">
                          <ImageIcon size={14} /> Images
                        </div>
                        <div className="mt-2 grid grid-cols-1 gap-2">
                          {images.slice(0, 5).map((url, idx) => (
                            <img
                              key={`${url}-${idx}`}
                              src={url}
                              alt=""
                              className="w-full rounded border border-gray-200"
                              style={{ maxHeight: 320, objectFit: 'cover' }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {selectedItem.cta_url ? (
                      <div className="pt-1">
                        <a
                          href={selectedItem.cta_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-full rounded bg-[#4B57AA] text-white text-sm px-4 py-2 hover:bg-[#3b448f] transition-colors"
                        >
                          {selectedItem.cta_text || 'Open'}
                        </a>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((item) => {
                  const unread = !item.read_at;
                  const metaObj = safeParseJson(item?.meta);
                  const summary = getListSummary(metaObj, item?.message);

                  return (
                    <button
                      key={item.id}
                      className="w-full text-left px-3 py-1.5 hover:bg-gray-50 transition-colors"
                      onClick={() => onOpenNotification(item)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className={`text-[13px] text-[#0f172a] truncate ${unread ? 'font-semibold' : 'font-medium'}`}>
                              {item.title}
                            </div>
                            {unread ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#4B57AA] text-white text-[10px]">
                                New
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs text-[#64748b] mt-0.5 truncate">{summary}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] text-[#94a3b8]">
                            {item.sent_at ? new Date(item.sent_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {hasMore || loadingMore ? (
                  <div className="px-3 py-2">
                    <button
                      className="w-full text-xs border border-gray-200 rounded px-3 py-1.5 hover:bg-gray-50 transition-colors text-[#0f172a] disabled:opacity-60"
                      onClick={loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default NotificationsPopup;

